import { useEffect, useRef, useState, type RefObject } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Le scrim garde le clic de fermeture ; la pile de toasts reste annoncée.
const KEEP_ACTIVE = ".scrim, .toast-stack";

/** Comportement clavier d'une modale montée : focus initial dans la carte,
 * Tab piégé, Échap ferme, fond inerte, focus rendu au déclencheur au démontage.
 * Les écouteurs sont posés sur la carte : seule la modale qui a le focus réagit,
 * ce qui ferme la plus haute quand deux sont empilées. */
export function useDialog(
  ref: RefObject<HTMLElement>,
  onClose: () => void,
): void {
  // Lu au premier rendu, avant que la modale ou un autoFocus ne prenne le focus.
  const [trigger] = useState(() =>
    document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    const inerted: Element[] = [];
    for (
      let node: Element = dialog;
      node.parentElement && node !== document.body;
      node = node.parentElement
    ) {
      for (const sibling of Array.from(node.parentElement.children)) {
        if (
          sibling === node ||
          sibling.hasAttribute("inert") ||
          sibling.matches(KEEP_ACTIVE)
        )
          continue;
        sibling.setAttribute("inert", "");
        inerted.push(sibling);
      }
    }

    if (!dialog.contains(document.activeElement)) dialog.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !dialog) return;
      const items = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE),
      );
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const current = document.activeElement;
      if (e.shiftKey && (current === first || current === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && current === last) {
        e.preventDefault();
        first.focus();
      }
    }

    dialog.addEventListener("keydown", onKeyDown);
    return () => {
      dialog.removeEventListener("keydown", onKeyDown);
      for (const el of inerted) el.removeAttribute("inert");
      if (trigger?.isConnected) trigger.focus();
    };
  }, [ref, trigger]);
}
